import { useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useLazyOrganizationRolesQuery,
  useLazyOrganizationUsersQuery,
  useOrganizationRolesQuery,
  useOrganizationUsersQuery,
} from "../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import {
  useGetSharedReportInternallyQuery,
  useSharedReportInternallyMutation,
} from "../../api/network/reportApiServices";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { SharedReportInternallyPayload } from "../../api/types/Report";
import {
  LegacyUserEntityListingPayload,
  TLegacyUser,
  TLegacyUserWithAccount,
} from "../../api/types/User";
import {
  LegacyRoleEntityListingPayload,
  TLegacyUserRole,
} from "../../api/types/UserRole";
import svgIcons from "../../assets/svg/svgIcons";
import {
  AdminFormFieldAsyncDropdown,
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldDropdown,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldRadio,
  AdminFormFieldSubmit,
  PillItem,
  TSelectboxOption,
} from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import { useLoggedInUserData } from "../../utils/user";
import { useReportUtils } from "./hooks/reportUtils";
import {
  shareReportInternallyInitialValues,
  shareReportInternallyValidationSchema,
} from "./validation";
import { asyncDebounce } from "../../utils/common";
import { GroupBase, OptionsOrGroups } from "react-select";

type ModalShareReportInternallyProps = {
  reportId: number;
};

const ModalShareReportInternally: FC<ModalShareReportInternallyProps> = ({
  reportId,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.shareReportInternally",
  });
  const dispatch = useDispatch();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );

  const {
    data: dataSharedReportInternally,
    isFetching: isFetchingSharedReportInternally,
  } = useGetSharedReportInternallyQuery(
    { report: reportId },
    { skip: !reportId }
  );

  const { results } = dataSharedReportInternally || {};

  const { refetchCustomReportListToStore } = useReportUtils();

  // share report internally mutation
  const [sharedReportInternallyAPITrigger] =
    useSharedReportInternallyMutation();

  //formik
  const formik = useFormik({
    initialValues: shareReportInternallyInitialValues,
    validationSchema: shareReportInternallyValidationSchema,
    onSubmit: (values) => {
      //prepare payload
      const payload: SharedReportInternallyPayload = {
        report: reportId,
        type: 1,
      };
      if (values.share_to === "roles") {
        payload.type = 2;
        payload.report_internal_share_roles = values.selected_roles.map(
          (role: string | { label: string; value: string }) => ({
            role_id: typeof role === "string" ? role : role.value,
          })
        );
      }

      if (values.share_to === "selected_users") {
        payload.type = 3;
        payload.report_internal_share_users = values.selected_users.map(
          (user: string | { label: string; value: string }) => ({
            user_id: typeof user === "string" ? user : user.value,
          })
        );
      }
      sharedReportInternallyAPITrigger(payload)
        .unwrap()
        .then((data) => {
          if (!!data?.id) {
            // changes the checkcase condition
            dispatch(
              setModalsData({
                ...modalsState,
                showInternalShareReportModal: false,
              })
            );
            toast.success(t("toast.create_success"), { autoClose: 10000 });

            // re-fetch custom reports and update store
            refetchCustomReportListToStore();
          } else {
            toast.error(t("toast.create_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.create_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  if (APP_CONFIG.DEBUG.ALL) console.log("dataSharedReportInternally", results);
  // prefill formik values
  useEffect(() => {
    if (!!dataSharedReportInternally) {
      const result = results?.[0];
      console.log("result", result);
      formik.setValues({
        ...formik.values,
        share_to:
          result?.type === 1
            ? "all_users"
            : result?.type === 2
              ? "roles"
              : "selected_users", // || "all_users",
        selected_roles:
          result?.report_internal_share_roles?.map((role: any) => ({
            label: `${role.role_id}`,
            value: `${role.role_id}`,
          })) || [],
        selected_users:
          result?.report_internal_share_users?.map((user: any) => ({
            label: `${user.user_id}`,
            value: `${user.user_id}`,
          })) || [],
      });
    }
  }, [dataSharedReportInternally]);

  //formik
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
    resetForm,
    submitForm,
  } = formik;

  const hideModal = () => {
    dispatch(
      setModalsData({ ...modalsState, showInternalShareReportModal: false })
    );
  };

  //ROLES -- start -----------------------------------------------------

  // prepare role query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgRolesQueryParams, setOrgRolesQueryParams] =
    useState<LegacyRoleEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      search: "",
    });

  // fetch organization roles
  const { data: dataOrgRoles, isFetching: isFetchingOrgROles } =
    useOrganizationRolesQuery(orgRolesQueryParams);

  // ROLES -- end -----------------------------------------------------

  // USERS -- start -----------------------------------------------------

  // selected users mechanism

  // prepare user query params
  const [orgUsersQueryParams, setOrgUsersQueryParams] =
    useState<LegacyUserEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      search: "",
    });

  // fetch organization users
  const { data: dataOrgUsers, isFetching: isFetchingOrgUsers } =
    useOrganizationUsersQuery(orgUsersQueryParams);

  // fetch searched users
  const [orgUsersApiTrigger, { isFetching: isFetchingUsers }] =
    useLazyOrganizationUsersQuery();

  // USERS -- end -----------------------------------------------------

  const [orgUsersQuery, { isLoading: isLoadingUsers }] =
    useLazyOrganizationUsersQuery();
  const [orgRolesQuery, { isLoading: isLoadingRoles }] =
    useLazyOrganizationRolesQuery();

  const loadOptionsHandlerUser = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: driversQueryResponse } = await orgUsersQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.USER_DROPDOWN.join(","), // search by userid only
    });

    if (!driversQueryResponse || !driversQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = driversQueryResponse as {
      results: TLegacyUserWithAccount[];
    };
    return {
      options: !!results
        ? (results || ([] as TLegacyUserWithAccount[]))
            .filter(
              (item: TLegacyUserWithAccount) =>
                item.userid !== loggedInUserData?.user?.userid
            ) // exclude current user
            .filter(
              (item: TLegacyUserWithAccount) =>
                !values.selected_users
                  .map((item) =>
                    typeof item === "string" ? item : item?.value
                  )
                  .includes(`${item.userid}`)
            ) // exclude already selected users
            .map(
              (item: TLegacyUserWithAccount, index: number) =>
                ({
                  value: `${item.userid}`,
                  label: `${item.userid}`,
                }) as TSelectboxOption
            )
        : [],
      hasMore: driversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerUser = asyncDebounce(
    loadOptionsHandlerUser,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const loadOptionsHandlerRoles = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: rolesQueryResponse } = await orgRolesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.ROLE_DROPDOWN.join(","), // search by roleid
    });

    if (!rolesQueryResponse || !rolesQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }

    const { results } = rolesQueryResponse as {
      results: TLegacyUserRole[];
    };

    return {
      options: !!results
        ? (results || ([] as TLegacyUserRole[]))
            .filter(
              (item: TLegacyUserRole) =>
                item.roleid !== loggedInUserData?.user?.userid
            )
            .filter(
              (item: TLegacyUserRole) =>
                !values.selected_roles
                  .map((item) =>
                    typeof item === "string" ? item : item?.value
                  )
                  .includes(`${item.roleid}`)
            )
            .map(
              (item: TLegacyUserRole, index: number) =>
                ({
                  value: `${item.roleid}`,
                  label: `${item.displayname || item.roleid}`,
                }) as TSelectboxOption
            )
        : [],
      hasMore: rolesQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };

  const debouncedLoadOptionsHandlerRoles = asyncDebounce(
    loadOptionsHandlerRoles,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  if (modalsState.showInternalShareReportModal === false) return null;
  return (
    <>
      <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
        {/* <div
          className="fixed w-full h-screen bg-modal-overlay z-overlay"
          onClick={hideModal}
        ></div> */}
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[480px] z-modal">
          <form
            className="p-8 bg-gray-100 grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={handleSubmit}
          >
            <h2 className="col-span-6 text-2xl font-bold text-heading-black">
              {t("heading")}
            </h2>
            {isFetchingSharedReportInternally ? (
              <div className="flex justify-between items-center w-full h-72">
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-opacity-90 z-50`}
                >
                  <img
                    src={svgIcons.LoadingAnimation}
                    alt="Loading animation"
                    className="w-12 h-12"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="col-span-6 grid grid-cols-12">
                  <AdminFormFieldRadio
                    label={t("all_users")}
                    name="share_to"
                    id="all_users"
                    type="radio"
                    value={"all_users"}
                    checked={values.share_to === "all_users"}
                    onChange={(e) => {
                      setFieldValue("share_to", e.target.value);
                    }}
                    customWrapperClass="col-span-4"
                  />
                  <AdminFormFieldRadio
                    label={t("roles")}
                    name="share_to"
                    id="roles"
                    type="radio"
                    value={"roles"}
                    checked={values.share_to === "roles"}
                    onChange={(e) => {
                      setFieldValue("share_to", e.target.value);
                    }}
                    customWrapperClass="col-span-4"
                  />

                  <AdminFormFieldRadio
                    label={t("select_users")}
                    name="share_to"
                    id="selected_users"
                    type="radio"
                    value={"selected_users"}
                    checked={values.share_to === "selected_users"}
                    onChange={(e) => {
                      setFieldValue("share_to", e.target.value);
                    }}
                    customWrapperClass="col-span-4"
                  />
                </div>

                {values.share_to === "roles" && (
                  <div className="col-span-6 grid grid-cols-6 gap-6">
                    <AdminFormFieldAsyncLazyDropdown
                      isMulti={true}
                      loadingData={isFetchingOrgROles}
                      label={t("select_role_to_add")}
                      id="roles"
                      name="roles"
                      value={values.selected_roles as any}
                      loadOptions={debouncedLoadOptionsHandlerRoles}
                      onChange={(e: any) => {
                        const newlySelectedRole = e;
                        if (!!e) {
                          setFieldValue("selected_roles", [
                            ...newlySelectedRole,
                          ]);
                        }
                      }}
                      detailsFormField={true}
                      placeholder={t("select_roles")}
                    />
                    {/* <AdminFormFieldPillList>
                      {values.selected_roles.map((role: string, i: number) => (
                        <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                          key={`pillItem_${i}`}
                          item={{ name: role } as PillItem}
                          onRemove={() => {
                            setFieldValue(
                              "selected_roles",
                              values.selected_roles.filter(
                                (selectedRoles: any) => selectedRoles !== role
                              )
                            );
                          }}
                          disabled={false}
                        />
                      ))}
                    </AdminFormFieldPillList> */}
                  </div>
                )}
                {values.share_to === "selected_users" && (
                  <div className="col-span-6 grid grid-cols-6 gap-6">
                    <AdminFormFieldAsyncLazyDropdown
                      isMulti={true}
                      value={values.selected_users as any}
                      key={`user_selector_`} // causes re-render on group selection
                      loadingData={
                        isFetchingOrgUsers || isFetchingUsers || isLoadingUsers
                      }
                      label={t("select_user_to_add")}
                      id="users"
                      name="users"
                      noOptionsMessage={() => t("user_selector_no_options")}
                      // defaultOption={{ value: `${values.group_selector}`, label: `${groupDisplayName(`${values.streetaddress}`, "-")}` }}
                      // defaultOptions={dataOrgUsers?.results
                      //   .filter(
                      //     (item: TLegacyUserWithAccount) =>
                      //       item.userid !== loggedInUserData?.user?.userid
                      //   ) // exclude current user
                      //   ?.filter(
                      //     (item) =>
                      //       !values.selected_users.includes(`${item.userid}`)
                      //   ) // exclude already selected users
                      //   ?.map((item: TLegacyUser) => ({
                      //     value: `${item.userid}`,
                      //     label: item.displayname || item.userid,
                      //   }))}
                      placeholder={t("select_users")}
                      loadOptions={debouncedLoadOptionsHandlerUser}
                      onChange={(e: any) => {
                        const newlySelected = e;
                        if (!!e) {
                          setFieldValue("selected_users", [...newlySelected]);
                        }
                      }}
                      additional={{ page: 1 }}
                      // onBlur={(e) => {
                      //   formikSetTouched("streetaddress", true);
                      //   handleBlur(e);
                      // }}
                      //error={errors.selected_users}
                      //touched={touched.selected_users}
                      //disabled={!userCanEdit || loadingData || values.all_groups}
                    />
                    {/* using multi select dropdown */}
                    {/* <AdminFormFieldPillList>
                      {values.selected_users.map((user: string, i: number) => (
                        <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                          key={`pillItem_${i}`}
                          item={{ name: user } as PillItem}
                          onRemove={() => {
                            setFieldValue(
                              "selected_users",
                              values.selected_users.filter(
                                (selectedUser: any) => selectedUser !== user
                              )
                            );
                          }}
                          disabled={false}
                        />
                      ))}
                    </AdminFormFieldPillList> */}
                  </div>
                )}

                <AdminFormFieldSubmit
                  label={tMain("share")}
                  variant="primary"
                  disabled={
                    isSubmitting ||
                    (values.share_to === "roles" &&
                      values.selected_roles.length === 0) ||
                    (values.share_to === "selected_users" &&
                      values.selected_users.length === 0)
                  }
                />

                <AdminFormFieldSubmit
                  label={tMain("cancel")}
                  type="button"
                  variant="danger-transparent"
                  disabled={isSubmitting}
                  onClick={hideModal}
                />
              </>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalShareReportInternally;
